var _react_refresh_temp_1;
_react_refresh_temp_1 = $RefreshSig$();
import { useCallback, useMemo, useSyncExternalStore } from 'react';
// We need this event, as StorageEvents are propagated to all contexts BUT
// the one that does any setting / removing. So if we want data to be fresh
// in the tab that set / removed an item, we need to dispatch an event manually.
// reference: https://developer.mozilla.org/en-US/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API#responding_to_storage_changes_with_the_storageevent
// The first paragraph explains how Storage events are sent only to contexts other than the one doing the change.
const dispatchStorageEvent = (key, newValue) => {
    window.dispatchEvent(new StorageEvent('storage', { key, newValue }));
};
// The dispatch of a Storage Event is required so that the current context gets updated,
// otherwise we get updated values in other contexts, but current context will not update.
const setItemInLocalStorage = (key, value) => {
    const stringifiedValue = JSON.stringify(value);
    window.localStorage.setItem(key, stringifiedValue);
    dispatchStorageEvent(key, stringifiedValue);
};
// The dispatch of a Storage Event is required so that the current context gets updated,
// otherwise we get updated values in other contexts, but the current one will not update.
const removeLocalStorageItem = (key) => {
    window.localStorage.removeItem(key);
    dispatchStorageEvent(key, null);
};
const getLocalStorageItem = (key) => window.localStorage.getItem(key);
// Subscription function that filters storage events by key. We need this so the hook
// will not rerender for every storage event - only for the key we care about
const createStorageSubscribe = (key) => (callback) => {
    const storageHandler = (event) => {
        if (event.key === key) {
            callback();
        }
    };
    window.addEventListener('storage', storageHandler);
    return () => window.removeEventListener('storage', storageHandler);
};
// TODO: Hook probably also needs some type of validator, so that values parsed from local storage
// do not need to be typecasted with 'as' and instead are safely parsed
/** Please use common sense and don't pass non-serializable values, otherwise we need to define some
complicated TS type */
export const useLocalStorage = (key) => {
    _react_refresh_temp_1();
    const store = useSyncExternalStore(createStorageSubscribe(key), () => getLocalStorageItem(key));
    const setValue = useCallback((value) => {
        try {
            setItemInLocalStorage(key, value);
        }
        catch (error) {
            // eslint-disable-next-line no-console -- Nothing to actually do, the hook will return undefined for value
            console.warn('Setting localStorage item failed', error);
        }
    }, [key]);
    // Removes the key and it's assigned value from local storage
    const removeValue = useCallback(() => {
        removeLocalStorageItem(key);
    }, [key]);
    const returnValue = useMemo(() => {
        const methods = {
            setValue,
            removeValue,
        };
        if (!store) {
            return { value: undefined, ...methods };
        }
        // If we add a validator, it must be used here as type guard
        try {
            return { value: JSON.parse(store), ...methods };
        }
        catch {
            // eslint-disable-next-line no-console -- Nothing to do here too, if some mangled the local storage content manually, we just return undefined
            console.warn('Failed to parse localStorage value');
            return { value: undefined, ...methods };
        }
    }, [removeValue, setValue, store]);
    return returnValue;
};
_react_refresh_temp_1(useLocalStorage, "jQJYIKbqNRbntjpT+J4BY957NzA=");
